import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { Upload, ChevronLeft, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { chunkGrantBodies, postGrantChunks } from '@/lib/grant-chunks';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ParsedRow {
  user_id: string;
  promotion_id: string;
  raw: string;
  error?: string;
}

interface PerPromotionResult {
  promotion_id: string;
  granted: number;
  skipped: number;
  /** Requests the grant took (the API WAF blocks bodies over 8 KB, so large grants are split). */
  requests: number;
  /** Users the server did not confirm because a request failed (send the same CSV again to finish). */
  unconfirmed: number;
  error?: string;
}

export function BulkGrantPage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [csv, setCsv] = useState('');
  const [reason, setReason] = useState('');
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<PerPromotionResult[] | null>(null);

  const parseCsv = () => {
    const lines = csv.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    // Skip header if it looks like one
    const startIdx =
      lines[0]?.toLowerCase().includes('user_id') || lines[0]?.toLowerCase().includes('email') ? 1 : 0;
    const out: ParsedRow[] = [];
    for (let i = startIdx; i < lines.length; i++) {
      const cols = lines[i].split(/[,\t]/).map((s) => s.trim().replace(/^"(.*)"$/, '$1'));
      if (cols.length < 2) {
        out.push({ user_id: '', promotion_id: '', raw: lines[i], error: 'Need 2 columns' });
        continue;
      }
      const [userId, promotionId] = cols;
      const errors: string[] = [];
      if (!UUID_RE.test(userId)) errors.push('user_id not UUID');
      if (!UUID_RE.test(promotionId)) errors.push('promotion_id not UUID');
      out.push({
        user_id: userId,
        promotion_id: promotionId,
        raw: lines[i],
        error: errors.join(', ') || undefined,
      });
    }
    setParsed(out);
    setResults(null);
  };

  const validRows = parsed.filter((r) => !r.error);
  const errorRows = parsed.filter((r) => r.error);
  const groups = new Map<string, string[]>();
  for (const r of validRows) {
    const arr = groups.get(r.promotion_id) ?? [];
    arr.push(r.user_id);
    groups.set(r.promotion_id, arr);
  }
  const reasonValid = reason.trim().length >= 3;
  const canSubmit = validRows.length > 0 && reasonValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResults([]);
    try {
      const idToken = await getIdToken();
      const out: PerPromotionResult[] = [];
      for (const [promotionId, userIds] of Array.from(groups.entries())) {
        try {
          // Chunks under the API WAF's 8 KB body limit (lib/grant-chunks.ts). The CSV
          // page is itself the bulk confirmation, as before: > 100 users sends
          // confirm_bulk on every chunk.
          const chunks = chunkGrantBodies(promotionId, userIds, reason.trim(), { confirmBulk: userIds.length > 100 });
          const r = await postGrantChunks(`${apiUrl}/v1/admin/coupons/grant`, idToken, chunks);
          out.push({
            promotion_id: promotionId,
            granted: r.granted,
            skipped: r.skipped.length,
            requests: r.chunksSent,
            unconfirmed: r.unconfirmed,
            error: r.error
              ? `${r.error}. ${r.granted} granted before it; ${r.unconfirmed} not confirmed. Send the same CSV again: users who already have the coupon are skipped.`
              : undefined,
          });
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          out.push({ promotion_id: promotionId, granted: 0, skipped: 0, requests: 0, unconfirmed: userIds.length, error: message });
        }
        setResults([...out]);
      }
      const totalGranted = out.reduce((s, r) => s + r.granted, 0);
      const totalSkipped = out.reduce((s, r) => s + r.skipped, 0);
      const totalUnconfirmed = out.reduce((s, r) => s + r.unconfirmed, 0);
      toast({
        title: totalUnconfirmed > 0 ? 'Bulk grant incomplete' : 'Bulk grant complete',
        description:
          `${totalGranted} granted, ${totalSkipped} skipped across ${out.length} promotions.` +
          (totalUnconfirmed > 0 ? ` ${totalUnconfirmed} not confirmed: see the errors, then send the same CSV again.` : ''),
        ...(totalUnconfirmed > 0 ? { variant: 'warning' as const } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/promotions')}
        className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1"
      >
        <ChevronLeft className="w-3 h-3" /> Back to promotions
      </button>
      <h1 className="text-2xl font-semibold flex items-center gap-2 mb-1">
        <Upload className="w-6 h-6" />
        Bulk Grant Coupons
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Paste a CSV with two columns: <code className="bg-muted px-1 rounded">user_id, promotion_id</code> (UUIDs
        both). Email resolution not supported in v1.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">CSV input</label>
          <textarea
            rows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={
              'user_id,promotion_id\n00000000-0000-0000-0000-000000000001,5e2196b1-e44a-4ede-a9b2-df23ad247109\n…'
            }
            className="w-full px-3 py-2 border rounded-md bg-background text-sm font-mono"
          />
          <button
            type="button"
            onClick={parseCsv}
            disabled={!csv.trim() || submitting}
            className="mt-2 px-3 py-1.5 border rounded-md text-sm hover:bg-muted/50 disabled:opacity-50"
          >
            Parse rows
          </button>
        </div>

        {parsed.length > 0 && (
          <div className="border rounded-md p-4 bg-muted/30">
            <div className="text-sm">
              <strong>{validRows.length}</strong> valid rows across {groups.size} promotions.
              {errorRows.length > 0 && (
                <span className="text-amber-600 ml-2">
                  <AlertCircle className="w-4 h-4 inline" /> {errorRows.length} errored.
                </span>
              )}
            </div>
            {errorRows.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">Show errors</summary>
                <ul className="mt-1 space-y-0.5">
                  {errorRows.slice(0, 20).map((r, i) => (
                    <li key={i} className="font-mono">
                      <span className="text-destructive">{r.error}</span>: {r.raw}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {parsed.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Reason <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Black Friday seed pool"
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
        )}

        {parsed.length > 0 && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Grant {validRows.length} coupons across {groups.size} promotions
          </button>
        )}

        {results && results.length > 0 && (
          <div className="border rounded-md p-4 mt-4">
            <h2 className="text-sm font-medium mb-3">Results</h2>
            <ul className="space-y-1 text-sm font-mono">
              {results.map((r) => (
                <li key={r.promotion_id} className="flex items-start gap-2">
                  {r.error ? (
                    <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  )}
                  <span className="break-all">
                    <span className="text-muted-foreground">{r.promotion_id.slice(0, 8)}&hellip;:</span>{' '}
                    {r.error
                      ? `error — ${r.error}`
                      : `${r.granted} granted, ${r.skipped} skipped${r.requests > 1 ? ` (${r.requests} requests)` : ''}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
